/**
 * NER-4488 — Catálogo de plantillas de combos.
 *
 * Una plantilla es un plano con "espacios" (slots). Cada espacio es:
 *   - `fixed`  → un item que SIEMPRE va incluido (con cantidad). Se guarda en
 *                `comboItems.fixed[]` del item combo.
 *   - `choice` → el cliente elige entre N items. Se materializa como un grupo
 *                de modificadores (min/max/required) adjunto al combo, porque es
 *                la única estructura que hoy el cliente puede elegir y que viaja
 *                en el pedido hasta la comanda.
 *
 * Los textos viven en i18n bajo `comboWizard.templates.{id}.*` y
 * `comboWizard.slots.{slotId}`. `hints` son fragmentos (sin acentos, en
 * minúscula) que se buscan en el nombre de las categorías del restaurante para
 * preseleccionar la categoría de cada espacio.
 *
 * Este módulo es puro (sin imports de React ni de services/api) para que los
 * tests y el mapper lo consuman barato.
 */

export const COMBO_TEMPLATE_VERSION = 1;

/** Marcador que usa el wizard para "crear la categoría Combos" al guardar. */
export const NEW_COMBOS_CATEGORY = '__new_combos_category__';

const HINTS = {
  main: [
    'hamburgues',
    'burger',
    'sandwich',
    'sanguch',
    'lomo',
    'pizza',
    'principal',
    'plato',
    'milanesa',
    'pollo',
    'wrap',
    'empanada',
  ],
  side: ['acompa', 'papas', 'guarnic', 'side', 'ensalada', 'fritas'],
  drink: ['bebida', 'drink', 'gaseosa', 'refresco', 'cerveza', 'jugo', 'agua', 'trago', 'vino'],
  starter: ['entrada', 'starter', 'picada', 'tapa', 'aperitivo'],
  dessert: ['postre', 'dessert', 'dulce', 'helado', 'torta'],
  shareable: ['compartir', 'parrilla', 'picada', 'pizza', 'tabla', 'familiar', 'bandeja'],
  hotDrink: ['cafe', 'café', 'caliente', 'te ', 'infusion', 'infusión', 'chocolate'],
  bakery: ['panader', 'medialuna', 'tostado', 'facturas', 'pan ', 'croissant', 'bakery'],
  pastry: ['pasteler', 'torta', 'cookie', 'budin', 'budín', 'brownie', 'dulce', 'alfajor'],
  juice: ['jugo', 'juice', 'licuado', 'smoothie', 'exprimido'],
};

/**
 * @typedef {Object} ComboSlot
 * @property {string} id            id estable del espacio dentro de la plantilla
 * @property {'fixed'|'choice'} kind
 * @property {string} labelKey      i18n `comboWizard.slots.*`
 * @property {string[]} hints       fragmentos para preseleccionar categoría
 * @property {number} [quantity]    solo fixed
 * @property {number} [min]         solo choice
 * @property {number} [max]         solo choice
 */

/**
 * @typedef {Object} ComboTemplate
 * @property {string} id
 * @property {string} icon              nombre de icono lucide-react
 * @property {number} defaultDiscountPct
 * @property {ComboSlot[]} slots
 */

/** @type {ComboTemplate[]} */
export const COMBO_TEMPLATES = [
  {
    id: 'classic',
    icon: 'Sandwich',
    defaultDiscountPct: 15,
    slots: [
      {
        id: 'main',
        kind: 'fixed',
        labelKey: 'comboWizard.slots.main',
        hints: HINTS.main,
        quantity: 1,
      },
      {
        id: 'drink',
        kind: 'choice',
        labelKey: 'comboWizard.slots.drink',
        hints: HINTS.drink,
        min: 1,
        max: 1,
      },
    ],
  },
  {
    id: 'with-side',
    icon: 'UtensilsCrossed',
    defaultDiscountPct: 15,
    slots: [
      {
        id: 'main',
        kind: 'fixed',
        labelKey: 'comboWizard.slots.main',
        hints: HINTS.main,
        quantity: 1,
      },
      {
        id: 'side',
        kind: 'choice',
        labelKey: 'comboWizard.slots.side',
        hints: HINTS.side,
        min: 1,
        max: 1,
      },
      {
        id: 'drink',
        kind: 'choice',
        labelKey: 'comboWizard.slots.drink',
        hints: HINTS.drink,
        min: 1,
        max: 1,
      },
    ],
  },
  {
    id: 'executive',
    icon: 'LayoutTemplate',
    defaultDiscountPct: 20,
    slots: [
      {
        id: 'starter',
        kind: 'choice',
        labelKey: 'comboWizard.slots.starter',
        hints: HINTS.starter,
        min: 1,
        max: 1,
      },
      {
        id: 'mainChoice',
        kind: 'choice',
        labelKey: 'comboWizard.slots.mainChoice',
        hints: HINTS.main,
        min: 1,
        max: 1,
      },
      {
        id: 'dessertOrDrink',
        kind: 'choice',
        labelKey: 'comboWizard.slots.dessertOrDrink',
        hints: [...HINTS.dessert, ...HINTS.drink],
        min: 1,
        max: 1,
      },
    ],
  },
  {
    id: 'for-two',
    icon: 'Users',
    defaultDiscountPct: 15,
    slots: [
      {
        id: 'mains',
        kind: 'choice',
        labelKey: 'comboWizard.slots.mains',
        hints: HINTS.main,
        min: 2,
        max: 2,
      },
      {
        id: 'drinks',
        kind: 'choice',
        labelKey: 'comboWizard.slots.drinks',
        hints: HINTS.drink,
        min: 2,
        max: 2,
      },
    ],
  },
  {
    id: 'family',
    icon: 'PartyPopper',
    defaultDiscountPct: 15,
    slots: [
      {
        id: 'shareable',
        kind: 'fixed',
        labelKey: 'comboWizard.slots.shareable',
        hints: HINTS.shareable,
        quantity: 1,
      },
      {
        id: 'bigSide',
        kind: 'fixed',
        labelKey: 'comboWizard.slots.bigSide',
        hints: HINTS.side,
        quantity: 1,
      },
      {
        id: 'drinks',
        kind: 'choice',
        labelKey: 'comboWizard.slots.drinks',
        hints: HINTS.drink,
        min: 2,
        max: 2,
      },
    ],
  },
  {
    id: 'breakfast',
    icon: 'Sun',
    defaultDiscountPct: 10,
    slots: [
      {
        id: 'hotDrink',
        kind: 'choice',
        labelKey: 'comboWizard.slots.hotDrink',
        hints: HINTS.hotDrink,
        min: 1,
        max: 1,
      },
      {
        id: 'bakery',
        kind: 'choice',
        labelKey: 'comboWizard.slots.bakery',
        hints: HINTS.bakery,
        min: 1,
        max: 1,
      },
      {
        id: 'juice',
        kind: 'choice',
        labelKey: 'comboWizard.slots.juice',
        hints: HINTS.juice,
        min: 0,
        max: 1,
      },
    ],
  },
  {
    id: 'snack',
    icon: 'Coffee',
    defaultDiscountPct: 10,
    slots: [
      {
        id: 'hotDrink',
        kind: 'choice',
        labelKey: 'comboWizard.slots.hotDrink',
        hints: HINTS.hotDrink,
        min: 1,
        max: 1,
      },
      {
        id: 'pastry',
        kind: 'choice',
        labelKey: 'comboWizard.slots.pastry',
        hints: HINTS.pastry,
        min: 1,
        max: 1,
      },
    ],
  },
  {
    id: 'custom',
    icon: 'Wand2',
    defaultDiscountPct: 0,
    slots: [],
  },
];

export const CUSTOM_TEMPLATE_ID = 'custom';

/** Devuelve la plantilla por id (o undefined). */
export function getComboTemplate(templateId) {
  return COMBO_TEMPLATES.find((tpl) => tpl.id === templateId);
}

/**
 * Espacio genérico para la plantilla "Personalizado" (o para agregar espacios
 * extra a cualquier plantilla). `index` se usa para el id estable.
 */
export function makeCustomSlot(kind, index) {
  const id = `${kind}-${index}`;
  if (kind === 'fixed') {
    return { id, kind, labelKey: 'comboWizard.slots.customFixed', hints: [], quantity: 1 };
  }
  return {
    id,
    kind: 'choice',
    labelKey: 'comboWizard.slots.customChoice',
    hints: [],
    min: 1,
    max: 1,
  };
}
